import React, { useCallback, useContext, useMemo, useState } from "react";
import MenuContext from "../../../Context/Menu/MenuContext";
import {
  Avatar,
  Box,
  InputAdornment,
  List,
  ListItem,
  TextField,
  Tooltip,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import AuthContext from "../../../Context/Auth/AuthContext";
import { withRouter } from "react-router-dom";
import { Cancel, ExpandLess, ExpandMore, Search } from "@mui/icons-material";
import { hacerIcono } from "../../../config/const";

const normalize = (str) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

const removeDuplicates = (array) => {
  let ids = array.map((obj) => obj.sidId);
  return array.filter((obj, index) => {
    return ids.indexOf(obj.sidId) === index;
  });
};

const getParents = (children, allData, allParents = []) => {
  const currentParents = allData.filter((padre) =>
    children.some((child) => child.padre === padre.sidId),
  );
  if (currentParents.length === 0) {
    return allParents;
  }
  allParents.push(...currentParents);
  return getParents(currentParents, allData, allParents);
};

const Sidebar = (props) => {
  const { rows } = useContext(MenuContext);
  const { usuario } = useContext(AuthContext);
  const { open: openSidebar, mobile, handleDrawerToggle, buscador } = props;
  const [openExpand, setOpenExpand] = useState({});
  const theme = useTheme();

  const categorias = useMemo(
    () => rows.filter((item) => item.sidInfo.tipo === "categoria"),
    [rows],
  );

  const subCategorias = useMemo(() => {
    if (categorias.length === 0) return [];
    return rows
      .filter((item) => item.sidInfo.tipo === "subCategoria")
      .map((item) => ({
        ...item,
        padre:
          categorias.find(
            (padre) => padre.sidInfo.path === item.sidInfo.categoria,
          )?.sidId ?? 0,
      }));
  }, [rows, categorias]);

  const subsubCategorias = useMemo(() => {
    if (subCategorias.length === 0) return [];
    return rows
      .filter((item) => item.sidInfo.tipo === "subsubCategoria")
      .map((item) => ({
        ...item,
        padre:
          subCategorias.find(
            (padre) => padre.sidInfo.path === item.sidInfo.subcategoria,
          )?.sidId ?? 0,
      }));
  }, [rows, subCategorias]);

  const sub_sub_subCategorias = useMemo(() => {
    if (subsubCategorias.length === 0) return [];
    return rows
      .filter((item) => item.sidInfo.tipo === "sub_sub_subCategoria")
      .map((item) => ({
        ...item,
        padre:
          subsubCategorias.find(
            (padre) => padre.sidInfo.path === item.sidInfo.subsubCategoria,
          )?.sidId ?? 0,
      }));
  }, [rows, subsubCategorias]);

  const allRows = useMemo(
    () => [
      ...categorias,
      ...subCategorias,
      ...subsubCategorias,
      ...sub_sub_subCategorias,
    ],
    [categorias, subCategorias, subsubCategorias, sub_sub_subCategorias],
  );

  const [open, setOpen] = useState({});
  const [busqueda, setbusqueda] = useState("");

  const onLeaveSearch = useCallback(() => {
    setOpen({});
    setbusqueda("");
  }, []);

  const dataFilter = useMemo(() => {
    const res = allRows.filter((i) => {
      const tienePermiso = i.sidInfo.ver.some((permiso) =>
        usuario.rol.includes(permiso),
      );
      if (!tienePermiso) return false;
      if (i.sidInfo.bloquearAreaDepartamento === "SI") {
        return i.sidInfo.areasDepartamento.some(
          (itemArea) => itemArea === usuario.areadepartamento,
        );
      }
      return true;
    });

    if (busqueda !== "") {
      const children = allRows.filter((page) =>
        normalize(page.sidInfo.component).includes(busqueda),
      );
      const parents = getParents(children, allRows);
      const uniqueArray = removeDuplicates([...children, ...parents]);
      const object = {};
      uniqueArray.forEach((pageUnique) => {
        object[pageUnique.sidId] = true;
      });
      setOpen(object);
      return uniqueArray;
    } else {
      setOpen({});
      return res;
    }
  }, [usuario, allRows, busqueda]);

  const handleClick = (e, a) => {
    setOpenExpand({ ...openExpand, [e]: !a });
  };

  function activeRoute(routeName) {
    return props.history.location.pathname.indexOf(routeName) > -1;
  }

  const getDepth = (path) => {
    const parts = path.split("/").filter(Boolean);
    if (parts.length <= 1) return 0;
    return parts.length - 1;
  };

  const generarRutas = (
    data,
    openSidebar,
    setOpen,
    open,
    padreId,
    path = "/Seguridad",
  ) => {
    const depth = getDepth(path) - 1;
    return data
      .filter(({ padre }) => padre === padreId)
      .map((page) => {
        const newPath = path + page.sidInfo.path;
        const isActive = activeRoute(newPath);

        return (
          <React.Fragment key={page.sidId}>
            <Tooltip
              title={openSidebar ? "" : page.sidInfo.component}
              placement="right"
              arrow
            >
              <ListItem
                disablePadding
                sx={{
                  mx: 0.5,
                  my: 0.15,
                  borderRadius: 1.5,
                  overflow: "hidden",
                  backgroundColor: isActive
                    ? alpha(theme.palette.secondary.main, 0.1)
                    : "transparent",
                  borderLeft: isActive
                    ? `3px solid ${theme.palette.secondary.main}`
                    : "3px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: isActive
                      ? alpha(theme.palette.secondary.main, 0.12)
                      : alpha(theme.palette.secondary.main, 0.05),
                  },
                }}
              >
                <ListItemButton
                  onClick={() => {
                    if (mobile) handleDrawerToggle();
                    props.history.push(newPath);
                  }}
                  sx={{
                    minHeight: 38,
                    justifyContent: openSidebar ? "initial" : "center",
                    px: depth > 0 ? 1 + depth * 0.75 : 1,
                    py: 0.25,
                    borderRadius: 1,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      backgroundColor: isActive ? "" : "",
                      borderRadius: 1.5,
                      minWidth: 0,
                      p: 0.0,
                      mr: openSidebar ? 1.2 : "auto",
                      justifyContent: "center",
                      transition: "all 0.2s ease",
                      "& svg": { fontSize: "1.1rem" },
                    }}
                  >
                    {page.sidInfo.imagenIcono ? (
                      <Avatar
                        src={page.sidInfo.imagenIcono}
                        variant="rounded"
                        sx={{ width: 24, height: 24 }}
                        imgProps={{ sx: { objectFit: "contain" } }}
                      />
                    ) : (
                      hacerIcono(page.sidInfo.icon, "blue", {
                        width: 24,
                        height: 24,
                      })
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={page.sidInfo.name}
                    primaryTypographyProps={{
                      color: isActive ? "secondary" : "text.secondary",
                      fontWeight: isActive ? 700 : 500,
                      fontSize: depth === 0 ? ".82rem" : ".78rem",
                      noWrap: true,
                    }}
                    sx={{
                      opacity: openSidebar ? 1 : 0,
                      transition: "opacity 0.2s ease",
                    }}
                  />
                </ListItemButton>
                {page.sidInfo.expandible && openSidebar && (
                  <IconButton
                    aria-label="Expandir"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpen({ ...open, [page.sidId]: !open[page.sidId] });
                    }}
                    sx={{
                      mr: 0.5,
                      color: alpha(theme.palette.text.primary, 0.5),
                      "&:hover": {
                        color: theme.palette.primary.main,
                      },
                      transition: "color 0.2s",
                    }}
                  >
                    {open[page.sidId] ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                )}
              </ListItem>
            </Tooltip>
            {page.sidInfo.expandible && (
              <Collapse
                in={open[page.sidId]}
                timeout="auto"
                unmountOnExit={false}
              >
                <Box
                  sx={{
                    ml: 2,
                    borderLeft: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.15,
                    )}`,
                    pl: 0.5,
                  }}
                >
                  {generarRutas(
                    data,
                    openSidebar,
                    setOpen,
                    open,
                    page.sidId,
                    newPath,
                  )}
                </Box>
              </Collapse>
            )}
          </React.Fragment>
        );
      });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {openSidebar && (
        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <TextField
            onChange={(e) => setbusqueda(e.target.value)}
            placeholder="Buscar menu..."
            variant="outlined"
            color="primary"
            size="small"
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Escape") onLeaveSearch();
            }}
            value={busqueda}
            name="search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    sx={{ fontSize: "1.1rem", color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {busqueda !== "" && (
                    <IconButton
                      aria-label="Cancelar Busqueda"
                      onClick={onLeaveSearch}
                      size="small"
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "error.main" },
                      }}
                    >
                      <Cancel sx={{ fontSize: "1.1rem" }} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2.5,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.07),
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                fontSize: "0.85rem",
              },
            }}
          />
        </Box>
      )}

      {!openSidebar && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <Tooltip title="Buscar" placement="right" arrow>
            <IconButton
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": { color: "primary.main" },
              }}
            >
              <Search />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Divider sx={{ mx: 1, my: 0.5 }} />

      <Box sx={{ overflow: "auto", flex: 1, py: 0.5 }}>
        <List disablePadding>
          {generarRutas(dataFilter, openSidebar, setOpen, open)}
        </List>
      </Box>
    </Box>
  );
};

export default withRouter(Sidebar);
